// context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authAPI } from '../services/api';
import { validateToken, isTokenAboutToExpire } from '../utils/auth-utils';

const AuthContext = createContext(null);

// 移除 useNavigate 和 useLocation
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // 检查认证状态，不再直接导航
  const checkAuthStatus = useCallback(() => {
    const isValid = validateToken();
    
    if (!isValid && isAuthenticated) {
      // 令牌无效但状态显示已认证，需要登出
      logout('您的登录已过期，请重新登录');
      return false;
    }
    
    return isValid;
  }, [isAuthenticated]);

  const login = async (username, password) => {
    try {
      const response = await authAPI.login(username, password);
      console.log('Login response:', response);
      
      // 保存 token
      localStorage.setItem('adminToken', response.token);
      
      // 创建用户对象
      const userObj = {
        username,
        email: response.email 
      };
      
      // 存储用户信息到本地存储
      localStorage.setItem('user', JSON.stringify(userObj));
      
      // 设置用户状态
      setUser(userObj);
      setIsAuthenticated(true);
      setError(null);
      
      console.log('Authentication successful, user:', userObj);
      
      return response;
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Login failed');
      throw error;
    }
  };

  // 登出函数不再处理导航，只处理状态
  const logout = useCallback((errorMessage = null) => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    
    if (errorMessage) {
      setError(errorMessage);
    } else {
      setError(null);
    }
    console.log('Logged out successfully');
  }, []);

  // 初始化认证状态
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('adminToken');
      const savedUser = localStorage.getItem('user');
      console.log('Initializing auth with token:', token);
      
      if (token && savedUser) {
        try {
          // 检查令牌有效性
          if (validateToken()) {
            const userObj = JSON.parse(savedUser);
            setUser(userObj);
            setIsAuthenticated(true);
          } else {
            // 令牌无效，清除状态
            localStorage.removeItem('adminToken');
            localStorage.removeItem('user');
            setError('登录已过期，请重新登录');
          }
        } catch (err) {
          console.error('Auth initialization error:', err);
          localStorage.removeItem('adminToken');
          localStorage.removeItem('user');
          setUser(null);
          setIsAuthenticated(false);
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  // 定期检查令牌有效性
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const checkTokenInterval = setInterval(() => {
      const isValid = validateToken();
      if (!isValid) {
        logout('您的登录已过期，请重新登录');
      } else if (isTokenAboutToExpire()) {
        // 可选：如果令牌即将过期，可以在此处实现自动续约
        console.log('Token is about to expire');
      }
    }, 60 * 1000); // 每分钟检查一次
    
    return () => clearInterval(checkTokenInterval);
  }, [isAuthenticated, logout]);

  // 监听认证状态变化
  useEffect(() => {
    console.log('Auth state updated:', { user, isAuthenticated, loading, error });
  }, [user, isAuthenticated, loading, error]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        logout,
        isAuthenticated,
        checkAuthStatus
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};